import type { FinancingRegime } from '@/api/generated-organizations'

/**
 * The signed-in organisation's last known `enabledFinancingRegimes`, kept in `localStorage` so
 * the side menu does not drop its Jeugdwet items on every full page load until
 * `GET /organizations/me` answers.
 *
 * This is display state only. Nothing that decides — a route guard, a write — may read it: the
 * loaded organisation is the answer there. Storage being unavailable (private mode, quota, a
 * value someone edited) reads as "nothing remembered", which is the behaviour without it.
 */
const KEY_PREFIX = 'scrivio.financingRegimes.'

export function readRememberedFinancingRegimes(organizationId: string): FinancingRegime[] | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + organizationId)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) && parsed.every((regime) => typeof regime === 'string')
      ? (parsed as FinancingRegime[])
      : null
  } catch {
    return null
  }
}

export function rememberFinancingRegimes(organizationId: string, regimes: readonly FinancingRegime[]): void {
  try {
    localStorage.setItem(KEY_PREFIX + organizationId, JSON.stringify(regimes))
  } catch {
    // Unavailable storage leaves nothing remembered.
  }
}

/** Forgets every organisation's, so the next user on this browser inherits none of them. */
export function forgetRememberedFinancingRegimes(): void {
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
    for (const key of keys) {
      if (key?.startsWith(KEY_PREFIX)) localStorage.removeItem(key)
    }
  } catch {
    // Unavailable storage holds nothing to forget.
  }
}
